import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { submitSurvey } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { FadeIn, Kicker, MaskedLine } from "../components/Reveal";
import SEO from "../components/SEO";

const inputCls = "w-full border border-border bg-background px-4 py-3 text-sm transition-colors placeholder:text-muted-foreground/60";

const PUBLISHABLE = ["farm_name", "country", "region", "website", "org_type", "varieties", "cultivation_area", "annual_production"];

export default function DatabaseSurvey() {
  const { d } = useI18n();
  const s = d.survey;
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    reference: params.get("ref") || "",
    email: "", farm_name: "", country: "", region: "", website: "",
    org_type: "", varieties: "", cultivation_area: "", annual_production: "",
  });
  const [consent, setConsent] = useState(null); // null | true | false
  const [fields, setFields] = useState([]);
  const [crossBorder, setCrossBorder] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleField = (f) => setFields((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await submitSurvey({
        ...form,
        publish_consent: consent === true,
        publish_fields: consent === true ? fields : [],
        cross_border_ack: crossBorder,
      });
      setDone(res.data);
      toast.success(s.toastOk);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(s.errNotFound);
      } else {
        toast.error(s.toastErr);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto max-w-4xl px-6 pt-36 pb-24 sm:px-10" data-testid="database-survey-page">
      <SEO page="survey" />
      <Kicker>{s.kicker}</Kicker>
      <h1 className="mt-8 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl" data-testid="survey-headline">
        <MaskedLine delay={0.2}>{s.title}</MaskedLine>
      </h1>
      <FadeIn delay={0.4}>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground">{s.intro}</p>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{s.editNote}</p>
      </FadeIn>

      {done ? (
        <FadeIn className="corner-ticks mt-12 border border-border bg-card/40 p-10" data-testid="survey-success">
          <CheckCircle2 size={36} className="text-foliage dark:text-stalk" />
          <h2 className="mt-6 font-serif text-2xl">{s.thanks}</h2>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground">
            {done.publish_consent ? s.successConsent : s.successNoConsent}
          </p>
          <p className="mt-6 inline-block border border-border px-4 py-2 font-mono text-xs tracking-[0.2em] text-crimson dark:text-stalk">REF · {done.reference}</p>
        </FadeIn>
      ) : (
        <form onSubmit={onSubmit} className="mt-12 space-y-10" data-testid="survey-form">
          <FadeIn className="corner-ticks border border-border bg-card/40 p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{s.verifyTitle}</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <input required value={form.reference} onChange={set("reference")} placeholder={s.refPh} className={inputCls} data-testid="survey-reference-input" />
              <input required type="email" value={form.email} onChange={set("email")} placeholder={s.emailPh} className={inputCls} data-testid="survey-email-input" />
            </div>
          </FadeIn>

          <FadeIn delay={0.05} className="corner-ticks border border-border bg-card/40 p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{s.aboutTitle}</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <input required value={form.farm_name} onChange={set("farm_name")} placeholder={s.farmPh} className={inputCls} data-testid="survey-farm-input" />
              <input required value={form.country} onChange={set("country")} placeholder={s.countryPh} className={inputCls} data-testid="survey-country-input" />
              <input value={form.region} onChange={set("region")} placeholder={s.regionPh} className={inputCls} data-testid="survey-region-input" />
              <input value={form.website} onChange={set("website")} placeholder={s.websitePh} className={inputCls} data-testid="survey-website-input" />
              <select required value={form.org_type} onChange={set("org_type")} className={`${inputCls} sm:col-span-2`} data-testid="survey-orgtype-select">
                <option value="">{s.orgTypePh}</option>
                {s.orgTypes.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} className="corner-ticks border border-border bg-card/40 p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{s.rhubarbTitle}</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <input value={form.varieties} onChange={set("varieties")} placeholder={s.varietiesPh} className={`${inputCls} sm:col-span-2`} data-testid="survey-varieties-input" />
              <input value={form.cultivation_area} onChange={set("cultivation_area")} placeholder={s.areaPh} className={inputCls} data-testid="survey-area-input" />
              <input value={form.annual_production} onChange={set("annual_production")} placeholder={s.productionPh} className={inputCls} data-testid="survey-production-input" />
            </div>
          </FadeIn>

          <FadeIn delay={0.15} className="corner-ticks border border-border bg-card/40 p-8" data-testid="survey-consent-section">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{s.consentTitle}</p>
            <div className="mt-5 space-y-3">
              <label className={`flex cursor-pointer items-start gap-3 border p-4 text-sm transition-colors ${consent === true ? "border-crimson bg-background" : "border-border"}`} data-testid="survey-consent-agree">
                <input type="radio" name="publish_consent" checked={consent === true} onChange={() => setConsent(true)} className="mt-1 h-4 w-4 accent-crimson" />
                {s.consentAgree}
              </label>
              <label className={`flex cursor-pointer items-start gap-3 border p-4 text-sm transition-colors ${consent === false ? "border-crimson bg-background" : "border-border"}`} data-testid="survey-consent-decline">
                <input type="radio" name="publish_consent" checked={consent === false} onChange={() => { setConsent(false); setFields([]); }} className="mt-1 h-4 w-4 accent-crimson" />
                {s.consentDecline}
              </label>
            </div>
            {consent === true && (
              <div className="mt-5" data-testid="survey-publish-fields">
                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{s.fieldsLabel}</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {PUBLISHABLE.map((f) => (
                    <label key={f} className="flex cursor-pointer items-center gap-3 border border-border px-4 py-2.5 text-sm" data-testid={`survey-field-${f}`}>
                      <input type="checkbox" checked={fields.includes(f)} onChange={() => toggleField(f)} className="h-4 w-4 accent-crimson" />
                      {s.fieldLabels[f]}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{s.noteNoConsent}</p>
          </FadeIn>

          <FadeIn delay={0.2} className="corner-ticks border border-border bg-card/40 p-8">
            <label className="flex cursor-pointer items-start gap-3 text-sm text-foreground/85">
              <input required type="checkbox" checked={crossBorder} onChange={(e) => setCrossBorder(e.target.checked)} className="mt-1 h-4 w-4 accent-crimson" data-testid="survey-crossborder-checkbox" />
              {s.crossBorder}
            </label>
            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{s.privateNote}</p>
          </FadeIn>

          {error && <p className="border border-crimson/40 bg-crimson/5 p-4 text-sm text-crimson dark:text-stalk" data-testid="survey-error">{error}</p>}

          <button
            type="submit"
            disabled={busy || consent === null || !crossBorder}
            data-testid="survey-submit-button"
            className="group flex w-full items-center justify-center gap-3 border border-crimson bg-crimson px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] text-parchment transition-colors hover:bg-transparent hover:text-crimson disabled:opacity-60"
          >
            {busy ? s.submitting : s.submit}
            <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </button>
        </form>
      )}
    </main>
  );
}
